import type { Unsubscribe } from 'firebase/firestore'
import { AuthenticationService } from '@core/services/authentication/authentication-service'
import {
  createNotification as createNotificationService,
  deleteNotification as deleteNotificationService,
  fetchUserNotifications,
  getUnreadNotificationsCount as getUnreadNotificationsCountService,
  loadUserNotifications as loadUserNotificationsService,
  markNotificationAsRead as markNotificationAsReadService,
  observeUnreadNotificationsCount as observeUnreadNotificationsCountService,
  observeUserNotifications as observeUserNotificationsService,
} from '@core/services/user-notifications/user-notifications-service'
import { enqueuePushForInAppNotification } from '@domain/features/push-notifications/push-notifications-feature'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import type { ActivityComment } from '@domain/models/activity-comments'
import type { UserNotification } from '@domain/models/user-notifications'
import { UserNotificationType } from '@domain/models/user-notifications'
import type { UserNotificationRefs } from '@domain/models/user-notifications'

export const NOTIFICATION_COMMENT_QUERY_PARAM = 'notificationCommentId'
export const NOTIFICATION_REPLY_QUERY_PARAM = 'notificationReplyId'

const COMMENT_CONTEXT = {
  ActivityExplore: 'activityExplore',
  Webinar: 'webinar',
  Podcast: 'podcast',
  InstagramReel: 'instagramReel',
  Symptom: 'symptom',
  WebinarSuggestion: 'webinarSuggestion',
} as const

type CommentContext = (typeof COMMENT_CONTEXT)[keyof typeof COMMENT_CONTEXT]

interface NotificationDestination {
  pathname: string
  search?: string
}

interface BaseCreateNotificationParams {
  forUserId: string
  senderId: string
  senderSlug?: string
}

interface CreateCommentLikedNotificationParams extends BaseCreateNotificationParams {
  comment: ActivityComment
}

interface CreateCommentAddedNotificationParams extends BaseCreateNotificationParams {
  comment: ActivityComment
}

interface CreateCommentReplyNotificationParams extends BaseCreateNotificationParams {
  comment: ActivityComment
}

interface CreateReplyLikedNotificationParams extends BaseCreateNotificationParams {
  comment: ActivityComment
  replyId: string
}

interface CreateWebinarQuestionLikedNotificationParams extends BaseCreateNotificationParams {
  webinarId: string
  questionId: string
}

interface CreateHackSuggestionSelectedWorksNotificationParams extends BaseCreateNotificationParams {
  symptomId: string
  whatWorkId: string
  whatWorkTitle?: string
}

interface CreateWebinarTopicUpvotedNotificationParams extends BaseCreateNotificationParams {
  suggestionId: string
  suggestionTitle?: string
}

function getAuthUserIdOrThrow(): string {
  const userId = AuthenticationService.getInstance().userId
  if (!userId) {
    throw new CoreError({
      cause: CoreErrorCause.Forbidden,
      context: 'No authenticated user available for notifications feature.',
    })
  }
  return userId
}

function normalizeSenderSlug(senderSlug: string | undefined): string | undefined {
  const normalizedSlug = senderSlug?.trim()
  return normalizedSlug && normalizedSlug.length > 0 ? normalizedSlug : undefined
}

function toActorSlugLabel(senderSlug: string | undefined): string {
  const normalizedSenderSlug = normalizeSenderSlug(senderSlug)
  if (normalizedSenderSlug) {
    return normalizedSenderSlug
  }
  return 'Someone'
}

function toCommentContext(comment: ActivityComment): CommentContext {
  if (comment.solutionRef && comment.pdSymptomId) {
    return COMMENT_CONTEXT.Symptom
  }

  if (comment.activityRefCollection === 'webinars') {
    return COMMENT_CONTEXT.Webinar
  }

  if (comment.activityRefCollection === 'podcast_episodes') {
    return COMMENT_CONTEXT.Podcast
  }

  if (comment.activityRefCollection === 'instagram_reels') {
    return COMMENT_CONTEXT.InstagramReel
  }

  if (comment.activityRefCollection === 'webinar_suggestions') {
    return COMMENT_CONTEXT.WebinarSuggestion
  }

  return COMMENT_CONTEXT.ActivityExplore
}

function createCommentRefs(comment: ActivityComment, replyId?: string): UserNotificationRefs {
  return {
    comment: comment.uid,
    commentReply: replyId,
    activity: comment.pdSymptomId ?? comment.activityRef,
    solution: comment.solutionRef,
  }
}

function createCommentRoute(
  notification: UserNotification,
  refs: UserNotificationRefs | undefined,
): NotificationDestination {
  const context = notification.commentType as CommentContext | undefined
  const commentId = refs?.comment
  const replyId = refs?.commentReply
  const activityId = refs?.activity ?? notification.activityRef
  const symptomId = refs?.activity
  const whatWorkId = refs?.solution

  if (context === COMMENT_CONTEXT.Symptom && symptomId && whatWorkId) {
    const params = new URLSearchParams()
    if (commentId) {
      params.set(NOTIFICATION_COMMENT_QUERY_PARAM, commentId)
    }
    if (replyId) {
      params.set(NOTIFICATION_REPLY_QUERY_PARAM, replyId)
    }
    return {
      pathname: `/hacks/${symptomId}/what-works/${whatWorkId}/comments`,
      search: params.toString() ? `?${params.toString()}` : undefined,
    }
  }

  if (!activityId) {
    return { pathname: '/news' }
  }

  const params = new URLSearchParams()
  if (commentId) {
    params.set(NOTIFICATION_COMMENT_QUERY_PARAM, commentId)
  }
  if (replyId) {
    params.set(NOTIFICATION_REPLY_QUERY_PARAM, replyId)
  }

  if (context === COMMENT_CONTEXT.Webinar) {
    return {
      pathname: `/webinars/${activityId}`,
      search: params.toString() ? `?${params.toString()}` : undefined,
    }
  }

  if (context === COMMENT_CONTEXT.Podcast) {
    return {
      pathname: `/prof-bas-bloem-weekly/${activityId}`,
      search: params.toString() ? `?${params.toString()}` : undefined,
    }
  }

  if (context === COMMENT_CONTEXT.InstagramReel) {
    return {
      pathname: `/dr-michael-okun-my-take/${activityId}`,
      search: params.toString() ? `?${params.toString()}` : undefined,
    }
  }

  if (context === COMMENT_CONTEXT.WebinarSuggestion) {
    return {
      pathname: `/webinars/suggestions/${activityId}/comments`,
      search: params.toString() ? `?${params.toString()}` : undefined,
    }
  }

  return {
    pathname: `/news/${activityId}`,
    search: params.toString() ? `?${params.toString()}` : undefined,
  }
}

function shouldSkipSelfNotification(forUserId: string, senderId: string): boolean {
  return forUserId === senderId
}

export class UserNotificationsFeature {
  public static resolveNotificationDestination(
    notification: UserNotification,
  ): NotificationDestination {
    const refs = notification.notificationRefs

    switch (notification.notificationType) {
      case UserNotificationType.CommentLiked:
      case UserNotificationType.CommentAdded:
      case UserNotificationType.CommentReplied:
      case UserNotificationType.ReplyLiked:
        return createCommentRoute(notification, refs)
      case UserNotificationType.WebinarQuestionLiked: {
        const webinarId = refs?.activity ?? notification.activityRef
        return { pathname: webinarId ? `/webinars/${webinarId}` : '/webinars' }
      }
      case UserNotificationType.HackSuggestionSelectedWorks: {
        const symptomId = refs?.activity
        if (!symptomId) {
          return { pathname: '/hacks' }
        }
        return { pathname: `/hacks/${symptomId}` }
      }
      case UserNotificationType.WebinarTopicUpvoted:
        return { pathname: '/webinars/suggestions' }
      default:
        return { pathname: '/news' }
    }
  }

  public static async getUserNotifications(userId?: string): Promise<UserNotification[]> {
    try {
      const resolvedUserId = userId ?? getAuthUserIdOrThrow()
      return await fetchUserNotifications(resolvedUserId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch user notifications.',
        underlyingException: error,
      })
    }
  }

  public static async loadUserNotifications(userId?: string): Promise<UserNotification[]> {
    try {
      const resolvedUserId = userId ?? getAuthUserIdOrThrow()
      return await loadUserNotificationsService(resolvedUserId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to load user notifications.',
        underlyingException: error,
      })
    }
  }

  public static observeUserNotifications(
    callback: (notifications: UserNotification[]) => void,
    onError?: (error: Error) => void,
    userId?: string,
  ): Unsubscribe {
    try {
      const resolvedUserId = userId ?? getAuthUserIdOrThrow()
      return observeUserNotificationsService(resolvedUserId, callback, onError)
    } catch (error) {
      onError?.(
        error instanceof CoreError
          ? error
          : new CoreError({
            cause: CoreErrorCause.Unknown,
            context: 'Failed to observe user notifications.',
            underlyingException: error,
          }),
      )
      return () => {}
    }
  }

  public static async createNotification(
    forUserId: string,
    notification: Partial<UserNotification>,
  ): Promise<string> {
    try {
      const notificationId = await createNotificationService({ forUserId, notification })
      const routeNotification: UserNotification = {
        uid: notificationId,
        ...notification,
      }
      const destination = UserNotificationsFeature.resolveNotificationDestination(routeNotification)

      void enqueuePushForInAppNotification({
        forUserId,
        notification: {
          ...notification,
          uid: notificationId,
        },
        destination,
      }).catch((pushError) => {
        console.error(
          `Failed to enqueue web push notification for in-app notification "${notificationId}".`,
          pushError,
        )
      })

      return notificationId
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to create notification.',
        underlyingException: error,
      })
    }
  }

  public static async deleteNotification(notificationId: string): Promise<void> {
    try {
      const userId = getAuthUserIdOrThrow()
      await deleteNotificationService(userId, notificationId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to delete notification "${notificationId}".`,
        underlyingException: error,
      })
    }
  }

  public static async markNotificationAsRead(notificationId: string): Promise<void> {
    try {
      const userId = getAuthUserIdOrThrow()
      await markNotificationAsReadService(userId, notificationId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to mark notification "${notificationId}" as read.`,
        underlyingException: error,
      })
    }
  }

  public static async getUnreadNotificationsCount(userId?: string): Promise<number> {
    try {
      const resolvedUserId = userId ?? getAuthUserIdOrThrow()
      return await getUnreadNotificationsCountService(resolvedUserId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch unread notifications count.',
        underlyingException: error,
      })
    }
  }

  public static observeUnreadNotificationsCount(
    callback: (count: number) => void,
    onError?: (error: Error) => void,
    userId?: string,
  ): Unsubscribe {
    try {
      const resolvedUserId = userId ?? getAuthUserIdOrThrow()
      return observeUnreadNotificationsCountService(resolvedUserId, callback, onError)
    } catch (error) {
      onError?.(
        error instanceof CoreError
          ? error
          : new CoreError({
            cause: CoreErrorCause.Unknown,
            context: 'Failed to observe unread notifications count.',
            underlyingException: error,
          }),
      )
      return () => {}
    }
  }

  public static async createCommentLikedNotification(
    params: CreateCommentLikedNotificationParams,
  ): Promise<string | null> {
    if (shouldSkipSelfNotification(params.forUserId, params.senderId)) {
      return null
    }

    const senderSlug = normalizeSenderSlug(params.senderSlug)
    const actorSlugLabel = toActorSlugLabel(senderSlug)
    const commentContext = toCommentContext(params.comment)

    return UserNotificationsFeature.createNotification(params.forUserId, {
      title: `${actorSlugLabel} liked your comment`,
      description: 'Open this activity to view your comment and discussion.',
      createdTime: new Date(),
      read: false,
      notificationType: UserNotificationType.CommentLiked,
      sender: params.senderId,
      userSlug: senderSlug,
      commentType: commentContext,
      likeType: 'like',
      notificationRefs: createCommentRefs(params.comment),
      activityRef: commentContext !== COMMENT_CONTEXT.Symptom
        ? params.comment.activityRef
        : undefined,
    })
  }

  public static async createCommentAddedNotification(
    params: CreateCommentAddedNotificationParams,
  ): Promise<string | null> {
    if (shouldSkipSelfNotification(params.forUserId, params.senderId)) {
      return null
    }

    const senderSlug = normalizeSenderSlug(params.senderSlug)
    const actorSlugLabel = toActorSlugLabel(senderSlug)
    const commentContext = toCommentContext(params.comment)
    const suggestionSuffix = commentContext === COMMENT_CONTEXT.WebinarSuggestion
      ? ' webinar suggestion'
      : ' activity'

    return UserNotificationsFeature.createNotification(params.forUserId, {
      title: `${actorSlugLabel} commented on your${suggestionSuffix}`,
      description: 'Open this activity to view the discussion.',
      createdTime: new Date(),
      read: false,
      notificationType: UserNotificationType.CommentAdded,
      sender: params.senderId,
      userSlug: senderSlug,
      commentType: commentContext,
      notificationRefs: createCommentRefs(params.comment),
      activityRef: commentContext !== COMMENT_CONTEXT.Symptom
        ? params.comment.activityRef
        : undefined,
    })
  }

  public static async createCommentReplyNotification(
    params: CreateCommentReplyNotificationParams,
  ): Promise<string | null> {
    if (shouldSkipSelfNotification(params.forUserId, params.senderId)) {
      return null
    }

    const senderSlug = normalizeSenderSlug(params.senderSlug)
    const actorSlugLabel = toActorSlugLabel(senderSlug)
    const commentContext = toCommentContext(params.comment)

    return UserNotificationsFeature.createNotification(params.forUserId, {
      title: `${actorSlugLabel} replied to your comment`,
      description: 'Open this activity to view the latest reply.',
      createdTime: new Date(),
      read: false,
      notificationType: UserNotificationType.CommentReplied,
      sender: params.senderId,
      userSlug: senderSlug,
      commentType: commentContext,
      notificationRefs: createCommentRefs(params.comment),
      activityRef: commentContext !== COMMENT_CONTEXT.Symptom
        ? params.comment.activityRef
        : undefined,
    })
  }

  public static async createReplyLikedNotification(
    params: CreateReplyLikedNotificationParams,
  ): Promise<string | null> {
    if (shouldSkipSelfNotification(params.forUserId, params.senderId)) {
      return null
    }

    const senderSlug = normalizeSenderSlug(params.senderSlug)
    const actorSlugLabel = toActorSlugLabel(senderSlug)
    const commentContext = toCommentContext(params.comment)

    return UserNotificationsFeature.createNotification(params.forUserId, {
      title: `${actorSlugLabel} liked your reply`,
      description: 'Open this activity to view the reply thread.',
      createdTime: new Date(),
      read: false,
      notificationType: UserNotificationType.ReplyLiked,
      sender: params.senderId,
      userSlug: senderSlug,
      commentType: commentContext,
      likeType: 'like',
      notificationRefs: createCommentRefs(params.comment, params.replyId),
      activityRef: commentContext !== COMMENT_CONTEXT.Symptom
        ? params.comment.activityRef
        : undefined,
    })
  }

  public static async createWebinarQuestionLikedNotification(
    params: CreateWebinarQuestionLikedNotificationParams,
  ): Promise<string | null> {
    if (shouldSkipSelfNotification(params.forUserId, params.senderId)) {
      return null
    }

    const senderSlug = normalizeSenderSlug(params.senderSlug)
    const actorSlugLabel = toActorSlugLabel(senderSlug)

    return UserNotificationsFeature.createNotification(params.forUserId, {
      title: `${actorSlugLabel} liked your webinar question`,
      description: 'Open the webinar details to view question activity.',
      createdTime: new Date(),
      read: false,
      notificationType: UserNotificationType.WebinarQuestionLiked,
      sender: params.senderId,
      userSlug: senderSlug,
      likeType: 'like',
      notificationRefs: {
        activity: params.webinarId,
        comment: params.questionId,
      },
    })
  }

  public static async createHackSuggestionSelectedWorksNotification(
    params: CreateHackSuggestionSelectedWorksNotificationParams,
  ): Promise<string | null> {
    if (shouldSkipSelfNotification(params.forUserId, params.senderId)) {
      return null
    }

    const senderSlug = normalizeSenderSlug(params.senderSlug)
    const actorSlugLabel = toActorSlugLabel(senderSlug)
    const whatWorkLabel = params.whatWorkTitle?.trim()
      ? `: ${params.whatWorkTitle.trim()}`
      : ''

    return UserNotificationsFeature.createNotification(params.forUserId, {
      title: `${actorSlugLabel} selected your hack suggestion as Works`,
      description: `Open the hack discussion${whatWorkLabel}.`,
      createdTime: new Date(),
      read: false,
      notificationType: UserNotificationType.HackSuggestionSelectedWorks,
      sender: params.senderId,
      userSlug: senderSlug,
      likeType: 'works',
      notificationRefs: {
        activity: params.symptomId,
        solution: params.whatWorkId,
      },
    })
  }

  public static async createWebinarTopicUpvotedNotification(
    params: CreateWebinarTopicUpvotedNotificationParams,
  ): Promise<string | null> {
    if (shouldSkipSelfNotification(params.forUserId, params.senderId)) {
      return null
    }

    const senderSlug = normalizeSenderSlug(params.senderSlug)
    const actorSlugLabel = toActorSlugLabel(senderSlug)
    const suggestionLabel = params.suggestionTitle?.trim()
      ? `: ${params.suggestionTitle.trim()}`
      : ''

    return UserNotificationsFeature.createNotification(params.forUserId, {
      title: `${actorSlugLabel} upvoted your webinar topic`,
      description: `Open webinar suggestions to view the latest votes${suggestionLabel}.`,
      createdTime: new Date(),
      read: false,
      notificationType: UserNotificationType.WebinarTopicUpvoted,
      sender: params.senderId,
      userSlug: senderSlug,
      likeType: 'upvote',
      notificationRefs: {
        activity: params.suggestionId,
      },
    })
  }
}

export function resolveNotificationDestination(
  notification: UserNotification,
): NotificationDestination {
  return UserNotificationsFeature.resolveNotificationDestination(notification)
}

export async function getUserNotifications(userId?: string): Promise<UserNotification[]> {
  return UserNotificationsFeature.getUserNotifications(userId)
}

export async function loadUserNotifications(userId?: string): Promise<UserNotification[]> {
  return UserNotificationsFeature.loadUserNotifications(userId)
}

export function observeUserNotifications(
  callback: (notifications: UserNotification[]) => void,
  onError?: (error: Error) => void,
  userId?: string,
): Unsubscribe {
  return UserNotificationsFeature.observeUserNotifications(callback, onError, userId)
}

export async function createUserNotification(
  forUserId: string,
  notification: Partial<UserNotification>,
): Promise<string> {
  return UserNotificationsFeature.createNotification(forUserId, notification)
}

export async function deleteUserNotification(notificationId: string): Promise<void> {
  return UserNotificationsFeature.deleteNotification(notificationId)
}

export async function markUserNotificationAsRead(notificationId: string): Promise<void> {
  return UserNotificationsFeature.markNotificationAsRead(notificationId)
}

export async function getUnreadNotificationsCount(userId?: string): Promise<number> {
  return UserNotificationsFeature.getUnreadNotificationsCount(userId)
}

export function observeUnreadNotificationsCount(
  callback: (count: number) => void,
  onError?: (error: Error) => void,
  userId?: string,
): Unsubscribe {
  return UserNotificationsFeature.observeUnreadNotificationsCount(callback, onError, userId)
}

export async function createCommentLikedNotification(
  params: CreateCommentLikedNotificationParams,
): Promise<string | null> {
  return UserNotificationsFeature.createCommentLikedNotification(params)
}

export async function createCommentAddedNotification(
  params: CreateCommentAddedNotificationParams,
): Promise<string | null> {
  return UserNotificationsFeature.createCommentAddedNotification(params)
}

export async function createCommentReplyNotification(
  params: CreateCommentReplyNotificationParams,
): Promise<string | null> {
  return UserNotificationsFeature.createCommentReplyNotification(params)
}

export async function createReplyLikedNotification(
  params: CreateReplyLikedNotificationParams,
): Promise<string | null> {
  return UserNotificationsFeature.createReplyLikedNotification(params)
}

export async function createWebinarQuestionLikedNotification(
  params: CreateWebinarQuestionLikedNotificationParams,
): Promise<string | null> {
  return UserNotificationsFeature.createWebinarQuestionLikedNotification(params)
}

export async function createHackSuggestionSelectedWorksNotification(
  params: CreateHackSuggestionSelectedWorksNotificationParams,
): Promise<string | null> {
  return UserNotificationsFeature.createHackSuggestionSelectedWorksNotification(params)
}

export async function createWebinarTopicUpvotedNotification(
  params: CreateWebinarTopicUpvotedNotificationParams,
): Promise<string | null> {
  return UserNotificationsFeature.createWebinarTopicUpvotedNotification(params)
}
